// R-CAL — reusable Jalali (Shamsi) MONTH calendar grid.
//
// A proper Persian month grid built ENTIRELY from i18n/jalali.ts (no Intl, no
// library, no hand-rolled date math): the header is JALALI_WEEKDAYS_FA starting
// شنبه, the day count comes from jalaliMonthLength (leap-aware Esfand), the
// leading blanks are computed from the first day's weekday, today is highlighted,
// and prev/next nav steps the Jalali month with JALALI_MONTHS_FA + a Persian-digit
// year. It is PURE + CONTROLLED: it takes `events` (each with a Gregorian ISO
// date), maps every event onto its Jalali cell via dateToJalali, shows a coloured
// dot per category on the day, and calls onSelectDay(isoDate) when a day is
// clicked. No backend calls inside — the parent owns data + the selected-day list.
//
// Responsive: a 7-column grid that shrinks its cells on narrow screens (down to
// 375px) without horizontal scroll; day cells stay ≥44px tall touch targets.

import React from "react";

import { Icon } from "../icons";
import {
  JALALI_MONTHS_FA,
  JALALI_WEEKDAYS_FA,
  dateToJalali,
  jalaliMonthLength,
  jalaliToDate,
  toFaDigits,
} from "../i18n/jalali";

export interface JalaliCalendarEvent {
  /** Gregorian ISO date (YYYY-MM-DD or a full ISO string). */
  date: string;
  title: string;
  category: string;
}

export interface JalaliCalendarProps {
  events: JalaliCalendarEvent[];
  /** Called with the clicked day's Gregorian ISO (YYYY-MM-DD). */
  onSelectDay?: (isoDate: string) => void;
  /** The currently-selected day's ISO (YYYY-MM-DD) — highlights that cell. */
  selectedIso?: string | null;
  /** Initial visible month (a JS Date). Defaults to today. */
  initialMonth?: Date;
  /** Resolve a category id → a CSS colour (for the day dots). */
  colorForCategory?: (category: string) => string;
  /**
   * Called whenever the visible month changes (and once on mount) with the
   * Gregorian YYYY-MM-DD bounds of that Jalali month. Lets a parent fetch a
   * month's events lazily (e.g. GET /v1/calendar/me?from&to). Additive +
   * optional — existing callers that don't pass it are unaffected.
   */
  onMonthChange?: (bounds: { from: string; to: string }) => void;
}

// getDay(): 0=Sun..6=Sat. Persian week starts Saturday → shift so Sat=0.
const persianWeekday = (d: Date): number => (d.getDay() + 1) % 7;

const pad2 = (n: number): string => String(n).padStart(2, "0");

// A Gregorian Y/M/D → the canonical YYYY-MM-DD key we compare events on.
const isoKey = (gy: number, gm: number, gd: number): string => `${gy}-${pad2(gm)}-${pad2(gd)}`;

// Normalise an event's stored date to a local Date, then back to a YYYY-MM-DD
// key, so "2026-06-20" and "2026-06-20T00:00:00.000Z" land on the same cell.
const eventDayKey = (iso: string): string | null => {
  if (!iso) return null;
  // Date-only strings: keep them literal (avoid the UTC-midnight → previous-day
  // shift that `new Date("2026-06-20")` causes in negative-offset zones).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return isoKey(d.getFullYear(), d.getMonth() + 1, d.getDate());
};

const DEFAULT_COLOR = "var(--accent)";

export const JalaliCalendar: React.FC<JalaliCalendarProps> = ({
  events,
  onSelectDay,
  selectedIso,
  initialMonth,
  colorForCategory,
  onMonthChange,
}) => {
  // The visible month (Jalali). Defaults to the initialMonth's (or today's) month.
  const [view, setView] = React.useState(() => {
    const base = dateToJalali(initialMonth ?? new Date());
    return { jy: base.jy, jm: base.jm };
  });

  const colorFor = colorForCategory ?? (() => DEFAULT_COLOR);

  // Notify the parent of the visible month's Gregorian bounds (once on mount and
  // on every month change) so it can fetch that month lazily. The callback is
  // read from a ref so a parent passing an inline lambda doesn't re-fire this.
  const onMonthChangeRef = React.useRef(onMonthChange);
  onMonthChangeRef.current = onMonthChange;
  React.useEffect(() => {
    const cb = onMonthChangeRef.current;
    if (!cb) return;
    const len = jalaliMonthLength(view.jy, view.jm);
    const first = jalaliToDate(view.jy, view.jm, 1);
    const last = jalaliToDate(view.jy, view.jm, len);
    cb({
      from: isoKey(first.getFullYear(), first.getMonth() + 1, first.getDate()),
      to: isoKey(last.getFullYear(), last.getMonth() + 1, last.getDate()),
    });
  }, [view.jy, view.jm]);

  // Group events by their YYYY-MM-DD key once per events change.
  const eventsByDay = React.useMemo(() => {
    const map = new Map<string, JalaliCalendarEvent[]>();
    for (const ev of events) {
      const key = eventDayKey(ev.date);
      if (!key) continue;
      const arr = map.get(key);
      if (arr) arr.push(ev);
      else map.set(key, [ev]);
    }
    return map;
  }, [events]);

  const stepMonth = (delta: number): void => {
    setView((v) => {
      let jm = v.jm + delta;
      let jy = v.jy;
      if (jm < 1) {
        jm = 12;
        jy -= 1;
      } else if (jm > 12) {
        jm = 1;
        jy += 1;
      }
      return { jy, jm };
    });
  };

  const goToday = (): void => {
    const t = dateToJalali(new Date());
    setView({ jy: t.jy, jm: t.jm });
  };

  const monthLen = jalaliMonthLength(view.jy, view.jm);
  const firstWeekday = persianWeekday(jalaliToDate(view.jy, view.jm, 1));
  const today = dateToJalali(new Date());

  // Cells: leading blanks for the first weekday, then 1..monthLen.
  const cells: (number | null)[] = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: monthLen }, (_, i) => i + 1),
  ];

  // The Gregorian ISO key for a given Jalali day in the visible month.
  const cellIso = (jd: number): string => {
    const d = jalaliToDate(view.jy, view.jm, jd);
    return isoKey(d.getFullYear(), d.getMonth() + 1, d.getDate());
  };

  return (
    <div className="card-flat" style={{ padding: 14 }}>
      <style>{`
        .jcal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
        .jcal-wh { text-align: center; font-size: 12px; color: var(--fg-mute); padding: 6px 0 8px; font-weight: 600; }
        .jcal-cell {
          position: relative; min-height: 46px; border-radius: 10px; border: 1px solid transparent;
          background: transparent; cursor: pointer; font-family: inherit; color: var(--fg);
          display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
          gap: 3px; padding: 6px 2px 4px; transition: background .12s ease, border-color .12s ease;
        }
        .jcal-cell:hover { background: var(--surface-2); }
        .jcal-cell.is-today { border-color: var(--accent); }
        .jcal-cell.is-selected { background: var(--accent); color: var(--accent-on); }
        .jcal-cell.is-selected:hover { background: var(--accent); }
        .jcal-num { font-size: 14px; font-weight: 600; line-height: 1.1; }
        .jcal-dots { display: flex; gap: 3px; flex-wrap: wrap; justify-content: center; min-height: 7px; }
        .jcal-dot { width: 6px; height: 6px; border-radius: 999px; }
        .jcal-cell.is-selected .jcal-dot { outline: 1px solid var(--accent-on); }
        @media (max-width: 420px) {
          .jcal-cell { min-height: 44px; padding: 5px 0 3px; }
          .jcal-num { font-size: 13px; }
        }
      `}</style>

      {/* month header + nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-label="ماه بعد"
          style={{ minWidth: 44, minHeight: 44 }}
          onClick={() => stepMonth(1)}
        >
          <Icon name="chevronRight" size={18} />
        </button>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <strong style={{ fontSize: 16 }}>
            {JALALI_MONTHS_FA[view.jm - 1]} {toFaDigits(view.jy)}
          </strong>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ minHeight: 28, fontSize: 11, padding: "2px 10px" }}
            onClick={goToday}
          >
            امروز
          </button>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-label="ماه قبل"
          style={{ minWidth: 44, minHeight: 44 }}
          onClick={() => stepMonth(-1)}
        >
          <Icon name="chevronLeft" size={18} />
        </button>
      </div>

      {/* weekday header */}
      <div className="jcal-grid" role="row">
        {JALALI_WEEKDAYS_FA.map((w, i) => (
          <div key={i} className="jcal-wh">
            {w}
          </div>
        ))}
      </div>

      {/* day cells */}
      <div className="jcal-grid">
        {cells.map((jd, i) => {
          if (jd === null) return <div key={`e${i}`} aria-hidden />;
          const iso = cellIso(jd);
          const dayEvents = eventsByDay.get(iso) ?? [];
          const isToday = today.jy === view.jy && today.jm === view.jm && today.jd === jd;
          const isSelected = !!selectedIso && selectedIso === iso;
          // De-dup category dots (≤4 shown) so a busy day stays readable.
          const cats: string[] = [];
          for (const ev of dayEvents) {
            if (!cats.includes(ev.category)) cats.push(ev.category);
            if (cats.length >= 4) break;
          }
          const cls = `jcal-cell${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}`;
          const aria =
            `${toFaDigits(jd)} ${JALALI_MONTHS_FA[view.jm - 1]}` +
            (dayEvents.length ? ` — ${toFaDigits(dayEvents.length)} رویداد` : "");
          return (
            <button
              key={jd}
              type="button"
              className={cls}
              aria-label={aria}
              aria-pressed={isSelected || undefined}
              onClick={() => onSelectDay?.(iso)}
            >
              <span className="jcal-num">{toFaDigits(jd)}</span>
              <span className="jcal-dots" aria-hidden>
                {cats.map((c) => (
                  <span key={c} className="jcal-dot" style={{ background: isSelected ? "var(--accent-on)" : colorFor(c) }} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default JalaliCalendar;
